import api from './api'

/**
 * Pasarelas de pago configuradas.
 *
 * Dos ámbitos sobre la misma tabla: `company` es la cuenta de la empresa activa
 * y `system` la global (la que usan las empresas que no tienen la suya). El
 * ámbito de sistema sólo lo puede tocar un usuario root.
 */
export type GatewayScopeType = 'company' | 'system'

export type GatewayProviderType = 'mercadopago' | 'wompi' | 'stripe' | 'sandbox_local'

export type GatewayModeType = 'sandbox' | 'live'

export interface PaymentGatewayType {
  id: number
  scope: GatewayScopeType
  provider: GatewayProviderType
  label: string
  mode: GatewayModeType
  publicKey?: string | null
  /**
   * Credenciales **enmascaradas** (`APP_US****3f2a`). Nunca llegan en claro: si
   * no se reescriben al guardar, el backend conserva las que ya tenía.
   */
  credentials: Record<string, string>
  settings: Record<string, unknown>
  isDefault: boolean
  isActive?: number | null
  /** false cuando el proveedor se puede configurar pero aún no tiene driver. */
  supported: boolean
}

export interface SaveGatewayPayloadType {
  provider: GatewayProviderType
  label: string
  mode?: GatewayModeType
  publicKey?: string | null
  /** En claro; el backend las cifra. Omitir para conservar las guardadas. */
  credentials?: Record<string, string>
  settings?: Record<string, unknown>
  isDefault?: boolean
  isActive?: number
  scope?: GatewayScopeType
  /** Administrar la pasarela de otra empresa (sólo root). */
  companyId?: string
}

/**
 * Las credenciales que puede pedir una pasarela. Es una unión cerrada y no un
 * `string` suelto porque el formulario declara un campo por cada una: si aquí
 * apareciera una clave que el esquema no conoce, el campo no validaría y nadie
 * se enteraría hasta que un cobro fallara.
 */
export type GatewayCredentialKeyType = 'accessToken' | 'privateKey' | 'secretKey' | 'eventsSecret' | 'integritySecret' | 'webhookSecret'

/** Qué credenciales pide cada pasarela, para pintar el formulario. */
export const GATEWAY_FIELDS: Record<GatewayProviderType, Array<{ key: GatewayCredentialKeyType; label: string; help?: string }>> = {
  mercadopago: [
    {
      key: 'accessToken',
      label: 'Access Token',
      help: 'Panel de desarrollador → tu aplicación → Credenciales. En pruebas empieza por TEST-.'
    },
    {
      key: 'webhookSecret',
      label: 'Clave secreta del webhook',
      help: 'Panel → Webhooks → Clave secreta. Sin ella se rechazan todos los avisos de pago.'
    }
  ],
  wompi: [
    {
      key: 'privateKey',
      label: 'Llave privada',
      help: 'Empieza por prv_test_ en pruebas y prv_prod_ en producción. Tiene que coincidir con el modo elegido arriba.'
    },
    {
      key: 'integritySecret',
      label: 'Secreto de integridad',
      help: 'Firma el checkout para que nadie pueda cambiar el monto por la URL (test_integrity_ / prod_integrity_).'
    },
    {
      key: 'eventsSecret',
      label: 'Secreto de eventos',
      help: 'Valida los avisos de pago (test_events_ / prod_events_). Sin él se rechazan todos.'
    }
  ],
  stripe: [
    { key: 'secretKey', label: 'Secret key' },
    { key: 'webhookSecret', label: 'Signing secret del webhook' }
  ],
  sandbox_local: [
    {
      key: 'webhookSecret',
      label: 'Secreto de pruebas',
      help: 'Pasarela simulada: no sale a internet. Sirve para probar el flujo completo.'
    }
  ]
}

export const GATEWAY_LABELS: Record<GatewayProviderType, string> = {
  mercadopago: 'MercadoPago',
  wompi: 'Wompi',
  stripe: 'Stripe',
  sandbox_local: 'Simulada (pruebas)'
}

export const paymentGatewayService = {
  async list(scope: GatewayScopeType = 'company', companyId?: string) {
    const { data } = await api.get('/payment-gateways', { params: { scope, companyId } })
    return data.data as PaymentGatewayType[]
  },

  /** Los proveedores que hoy tienen driver de verdad. */
  async providers() {
    const { data } = await api.get('/payment-gateways/providers')
    return data.data as GatewayProviderType[]
  },

  async create(payload: SaveGatewayPayloadType) {
    const { data } = await api.post('/payment-gateways', payload)
    return data.data as PaymentGatewayType
  },

  async update(id: number, payload: SaveGatewayPayloadType) {
    const { data } = await api.put(`/payment-gateways/${id}`, payload)
    return data.data as PaymentGatewayType
  },

  async setDefault(id: number, scope: GatewayScopeType = 'company', companyId?: string) {
    const { data } = await api.post(`/payment-gateways/${id}/default`, {}, { params: { scope, companyId } })
    return data.data as PaymentGatewayType
  },

  /** Comprueba las credenciales contra la pasarela. */
  async test(id: number, scope: GatewayScopeType = 'company', companyId?: string) {
    const { data } = await api.post(`/payment-gateways/${id}/test`, {}, { params: { scope, companyId } })
    return data.data as string
  },

  async delete(id: number, scope: GatewayScopeType = 'company', companyId?: string) {
    const { data } = await api.delete(`/payment-gateways/${id}`, { params: { scope, companyId } })
    return data.data as boolean
  }
}

export default paymentGatewayService
